import { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import au from 'date-fns/locale/en-AU';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import LogRocket from 'logrocket';

// routes
import Router from './routes';
// theme
import ThemeProvider from './theme';
// components
import ScrollToTop from './components/scroll-to-top';
// ga tracking
import { GoogleAnalyticsHead } from './ga';

import tokenManager from './utils/tokenManager';
import { preloadIconsAsync } from './utils/preloadIconsAsync';
import { useSyncNewUserMutation } from './slices/apis/userApi';
import LoadingPage from './pages/PageLoading';
import ErrorPage from './pages/PageError';

// ----------------------------------------------------------------------

export default function App() {
	const { getAccessTokenSilently, user: auth0User } = useAuth0();

	if (auth0User?.email) {
		LogRocket.identify(auth0User.email);
	}

	const location = useLocation();
	const ifSyncNewUser = auth0User?.logins_count < 3 && !location.search.includes('from=payment_success');

	const [
		syncNewUser,
		{ isLoading: isSyncNewUserLoading, isError: isSyncNewUserError, isSuccess: isSyncNewUserSuccess },
	] = useSyncNewUserMutation();

	tokenManager.setAccessTokenSilently(getAccessTokenSilently);

	useEffect(() => {
		if (ifSyncNewUser) {
			syncNewUser();
		}
	}, [syncNewUser, ifSyncNewUser]);

	useEffect(() => {
		async function load() {
			await preloadIconsAsync();
		}

		load();
	});

	if (!ifSyncNewUser) {
		return (
			<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={au}>
				<HelmetProvider>
					<GoogleAnalyticsHead />
					<ThemeProvider>
						<ScrollToTop />
						<Router />
					</ThemeProvider>
				</HelmetProvider>
			</LocalizationProvider>
		);
	}

	if (isSyncNewUserLoading) {
		return <LoadingPage message={"We're setting up your account."} />;
	}

	if (isSyncNewUserError) {
		return <ErrorPage />;
	}

	if (isSyncNewUserSuccess) {
		return (
			<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={au}>
				<HelmetProvider>
					<GoogleAnalyticsHead />
					<ThemeProvider>
						<ScrollToTop />
						<Router />
					</ThemeProvider>
				</HelmetProvider>
			</LocalizationProvider>
		);
	}

	return <div>Unhandled state.</div>;
}
